import { ListExpertCollectionsResponse } from '@cbinsights/collectionservice/collectionservice';
import { UseQueryResult, useMutation, useQuery } from 'react-query';
import { getAllStateValues } from 'client/modules/cbi-entity/services/general-info/apis/GetAllStateValues';
import { getAllInvestorCategoryValues } from 'client/modules/cbi-entity/services/general-info/apis/GetAllInvestorCategoryValues';
import {
  AllInvestorCategoryValues,
  AllStateValues,
  IdCountry,
} from '@cbinsights/cbientityservice/cbientityservice';
import {
  GetDataFeedConfigResponse,
  ListDataFeedConfigResponse,
} from '@cbinsights/externalapiservice/externalapiservice';
import {
  createDataFeedConfig,
  deleteDataFeedConfig,
  getDataFeedConfig,
  listDataFeedConfig,
  listExpertCollections,
  setDataFeedActive,
  updateDataFeedConfig,
} from './apis';

export const useListExpertCollections = (): UseQueryResult<
  ListExpertCollectionsResponse,
  Error
> =>
  useQuery<
    ListExpertCollectionsResponse,
    Error,
    ListExpertCollectionsResponse,
    ['ListExpertCollections']
  >({
    queryKey: ['ListExpertCollections'],
    queryFn: listExpertCollections,
    notifyOnChangeProps: [
      'data',
      'isFetching',
      'isFetched',
      'isFetchedAfterMount',
    ],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

export const useAllStateValues = (
  req: IdCountry
): UseQueryResult<AllStateValues, Error> =>
  useQuery<AllStateValues, Error, AllStateValues, ['AllStateValues', number]>({
    queryKey: ['AllStateValues', req?.idCountry],
    queryFn: () => getAllStateValues(req),
    notifyOnChangeProps: [
      'data',
      'isFetching',
      'isFetched',
      'isFetchedAfterMount',
    ],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

export const useCreateDataFeedConfigMutation = () =>
  useMutation(createDataFeedConfig);

export const useListDataFeedConfig = (
  idTeam: string
): UseQueryResult<ListDataFeedConfigResponse, Error> =>
  useQuery<
    ListDataFeedConfigResponse,
    Error,
    ListDataFeedConfigResponse,
    ['ListDataFeedConfigResponse', string]
  >({
    queryKey: ['ListDataFeedConfigResponse', idTeam],
    queryFn: () => listDataFeedConfig({ id_team: idTeam }),
    notifyOnChangeProps: [
      'data',
      'isFetching',
      'isFetched',
      'isFetchedAfterMount',
    ],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

export const useGetAllInvestorCategoryValues = (): UseQueryResult<
  AllInvestorCategoryValues,
  Error
> =>
  useQuery<
    AllInvestorCategoryValues,
    Error,
    AllInvestorCategoryValues,
    ['GetAllInvestorCategoryValues']
  >(['GetAllInvestorCategoryValues'], () => getAllInvestorCategoryValues(), {
    notifyOnChangeProps: ['data', 'isFetching', 'isFetchedAfterMount'],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

export const useDeleteDataFeedConfigMutation = () =>
  useMutation(deleteDataFeedConfig);

export const useSetDataFeedActiveMutation = () =>
  useMutation(setDataFeedActive);

export const useUpdateDataFeedConfigMutation = () =>
  useMutation(updateDataFeedConfig);

export const useGetDataFeedConfig = (
  idDataFeed: number
): UseQueryResult<GetDataFeedConfigResponse, Error> =>
  useQuery<
    GetDataFeedConfigResponse,
    Error,
    GetDataFeedConfigResponse,
    ['GetDataFeedConfig', number]
  >({
    queryKey: ['GetDataFeedConfig', idDataFeed],
    queryFn: () =>
      getDataFeedConfig({ id_data_feed: idDataFeed, include_inactive: true }),
    notifyOnChangeProps: [
      'data',
      'isFetching',
      'isFetched',
      'isFetchedAfterMount',
    ],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
